import { Subject } from 'rxjs';

export enum SubjectKeys {
  PageResize = 'pageResize',
  GlobalClick = 'globalClick',
  GlobalScroll = 'globalScroll',
  WindowResize = 'windowResize',
}

// 定义每个 Subject 对应的类型映射
type SubjectTypeMap = {
  [SubjectKeys.PageResize]: ResizeObserverEntry[];
  [SubjectKeys.GlobalClick]: MouseEvent;
  [SubjectKeys.GlobalScroll]: WheelEvent;
  [SubjectKeys.WindowResize]: void;
};

// 清理函数接口
interface CleanupFunction {
  (): void;
}

class SubjectManager {
  private subjects = {
    pageResize: undefined as Subject<ResizeObserverEntry[]> | undefined,
    globalClick: undefined as Subject<MouseEvent> | undefined,
    globalScroll: undefined as Subject<WheelEvent> | undefined,
    windowResize: undefined as Subject<void> | undefined,
  };

  // 存储清理函数
  private cleanupFunctions = new Map<SubjectKeys, CleanupFunction>();

  getSubject<T extends SubjectKeys>(name: T): Subject<SubjectTypeMap[T]> | undefined {
    try {
      if (!this.subjects[name]) {
        this.createSubject(name);
      }
      return this.subjects[name] as Subject<SubjectTypeMap[T]> | undefined;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to get subject ${name}:`, error);
      return undefined;
    }
  }

  // 清理指定的 Subject
  cleanup(name: SubjectKeys): void {
    const cleanup = this.cleanupFunctions.get(name);
    if (cleanup) {
      cleanup();
      this.cleanupFunctions.delete(name);
    }

    const subject = this.subjects[name];
    if (subject) {
      subject.complete();
      this.subjects[name] = undefined;
    }
  }

  // 清理所有 Subject
  cleanupAll(): void {
    Object.values(SubjectKeys).forEach(key => {
      this.cleanup(key);
    });
  }

  private createSubject(name: SubjectKeys): void {
    switch (name) {
      case SubjectKeys.PageResize:
        this.createPageResizeSubject();
        break;
      case SubjectKeys.GlobalClick:
        this.createGlobalClickSubject();
        break;
      case SubjectKeys.GlobalScroll:
        this.createGlobalScrollSubject();
        break;
      case SubjectKeys.WindowResize:
        this.createWindowResizeSubject();
        break;
    }
  }

  private createPageResizeSubject(): void {
    this.subjects.pageResize = new Subject<ResizeObserverEntry[]>();
    const resizeObserver = new ResizeObserver(entries => {
      try {
        this.subjects.pageResize?.next(entries);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in page resize subject:', error);
      }
    });

    resizeObserver.observe(document.body);

    // 存储清理函数
    this.cleanupFunctions.set(SubjectKeys.PageResize, () => {
      resizeObserver.disconnect();
    });
  }

  private createGlobalClickSubject(): void {
    this.subjects.globalClick = new Subject<MouseEvent>();

    const clickHandler = (event: MouseEvent) => {
      try {
        this.subjects.globalClick?.next(event);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in global click subject:', error);
      }
    };

    document.addEventListener('click', clickHandler);

    // 存储清理函数
    this.cleanupFunctions.set(SubjectKeys.GlobalClick, () => {
      document.removeEventListener('click', clickHandler);
    });
  }

  private createGlobalScrollSubject(): void {
    this.subjects.globalScroll = new Subject<WheelEvent>();

    const scrollHandler = (event: WheelEvent) => {
      try {
        this.subjects.globalScroll?.next(event);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in global scroll subject:', error);
      }
    };

    document.addEventListener('wheel', scrollHandler);

    // 存储清理函数
    this.cleanupFunctions.set(SubjectKeys.GlobalScroll, () => {
      document.removeEventListener('wheel', scrollHandler);
    });
  }

  private createWindowResizeSubject(): void {
    this.subjects.windowResize = new Subject<void>();

    const resizeHandler = () => {
      try {
        requestAnimationFrame(() => {
          this.subjects.windowResize?.next();
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in window resize subject:', error);
      }
    };

    window.addEventListener('resize', resizeHandler);

    // 存储清理函数
    this.cleanupFunctions.set(SubjectKeys.WindowResize, () => {
      window.removeEventListener('resize', resizeHandler);
    });
  }
}

export const subjectManager = new SubjectManager();

// 在页面卸载时清理资源
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    subjectManager.cleanupAll();
  });
}
